"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const GET_QUOTE_URL = "/upload";

// 与 PrecisionMachiningIntro / AxisMachineShop 一致的 Globe 图标
const GlobeIcon = ({ className }: { className?: string }) => (
	<svg
		className={`h-14 w-14 sm:h-16 sm:w-16 md:h-[72px] md:w-[72px] lg:h-20 lg:w-20 ${
			className ?? "text-[#0d68ea]"
		}`}
		viewBox="0 0 48 48"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="24" cy="24" r="20" />
		<ellipse cx="24" cy="24" rx="20" ry="8" />
		<ellipse cx="24" cy="24" rx="8" ry="20" />
	</svg>
);

interface PrecisionMachineShopSectionProps {
	/** 车间图片路径，可后续手动替换 */
	imageSrc: string;
}

export default function PrecisionMachineShopSection({ imageSrc }: PrecisionMachineShopSectionProps) {
	const t = useTranslations("precisionMachiningMachineShop");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题与 Globe 图标同一行，图标在该行内居中 */}
				<div className="relative mb-8 flex min-h-[72px] items-center sm:mb-10 sm:min-h-[80px] md:mb-12 md:min-h-[88px]">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
						<GlobeIcon className="!text-themePrimary400" />
					</div>
				</div>

				{/* 两列：左图 + 右文，各占一半 */}
				<div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-8 xl:gap-10">
					{/* 左侧图片 50% */}
					<div className="w-full lg:min-w-0 lg:flex-1">
						<div className="relative aspect-[4/3] w-full overflow-hidden shadow-lg lg:aspect-auto lg:h-full lg:min-h-[480px]">
							<Image
								src={imageSrc}
								alt={t("imageAlt")}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>
					</div>

					{/* 右侧文案 50% */}
					<div className="flex w-full min-w-0 flex-1 flex-col justify-center lg:min-w-0">
						<p className="mb-6 text-sm leading-relaxed text-black md:text-base">{t("paragraph1")}</p>
						<p className="mb-8 text-sm leading-relaxed text-black md:text-base">{t("paragraph2")}</p>
						<a
							href={GET_QUOTE_URL}
							target="_blank"
							rel="noopener noreferrer"
							className="group inline-flex w-fit items-center justify-center rounded bg-themePrimary400 px-5 py-3 text-sm font-medium text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white md:px-6 md:py-3.5 md:text-base"
						>
							{t("buttonText")}
						</a>
					</div>
				</div>
			</div>
		</section>
	);
}
